import { useAuth0 } from "@auth0/auth0-react";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import PageviewIcon from "@mui/icons-material/Pageview";
import {
    Avatar,
    Box,
    Button,
    CssBaseline,
    Divider,
    IconButton,
    List,
    ListItem,
    ListItemButton,
    ListItemIcon,
    Tooltip,
    Typography,
    Zoom,
} from "@mui/material";
import PropTypes from "prop-types";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { Link } from "react-router";

import { SearchInput, SkeletonLoader } from "@/atoms";
import { identifiersData, namesData, sidebarData, urlsData } from "@/data";
import { StyledSidebar, StyledSidebarHeader } from "@/styles";
import { theme } from "@/theme";
import { canDo, hasRole } from "@/utils";

import SidebarTab from "./tab";

const Sidebar = ({ open, setOpen }) => {
    const { t } = useTranslation();

    const sidebarActiveTab = useSelector((state) => state.sidebarActiveTabReducer.activeTab);

    const track = useSelector((state) => state.tracksReducer).playedTrack;

    const { isLoading, user } = useAuth0();

    const isAllowed = ({ hasRoles, oneOfPermissions, permissions, roles }) =>
        hasRoles ? hasRole(user, roles) : canDo(user, permissions, oneOfPermissions);

    const filteredSidebarData = sidebarData.tabs.filter(isAllowed).map((tab) => ({
        ...tab,
        ...(tab.children && { children: tab.children.filter(isAllowed) }),
    }));

    const [expandedTabs, setExpandedTabs] = useState([]);

    const toggleExpandedTab = (id) =>
        setExpandedTabs((previousExpandedTabs) =>
            previousExpandedTabs.includes(id)
                ? previousExpandedTabs.filter((expandedTabId) => expandedTabId !== id)
                : [...previousExpandedTabs, id],
        );

    // Keep the branch holding the current route open, whichever way the user landed on it.
    useEffect(() => {
        const { parentId } = sidebarActiveTab;

        if (!parentId) return;

        setExpandedTabs((previousExpandedTabs) =>
            previousExpandedTabs.includes(parentId) ? previousExpandedTabs : [...previousExpandedTabs, parentId],
        );
    }, [sidebarActiveTab]);

    const {
        ids: { button: buttonId, sidebar: sidebarId },
    } = identifiersData;

    const { closeSidebar: closeSidebarButtonName, search: searchButtonName } = namesData.buttons;

    return (
        <Box
            sx={{
                display: "flex",
            }}
        >
            <CssBaseline />
            <StyledSidebar
                open={open}
                variant="permanent"
                slotProps={{
                    paper: {
                        sx: { height: track?.downloadUri && "calc(100% - 60px) !important" },
                    },
                }}
            >
                <StyledSidebarHeader>
                    <Zoom in={open}>
                        <Link style={{ textDecoration: "none" }} to={urlsData.routes.home}>
                            {import.meta.env.VITE_SHOW_CMS_LOGO === "true" ? (
                                <Avatar
                                    alt={t("imageAlternatives.logo")}
                                    slotProps={{ img: { loading: "lazy" } }}
                                    src={import.meta.env.VITE_CMS_LOGO}
                                    sx={{
                                        display: open && "none",
                                        height: 20,
                                        marginRight: 1,
                                        width: 20,
                                    }}
                                />
                            ) : (
                                <Typography
                                    color={theme.palette.primary.main}
                                    variant="caption"
                                    sx={{
                                        fontSize: 18,
                                        textTransform: "capitalize",
                                    }}
                                >
                                    {import.meta.env.VITE_CMS_LOGO_NAME}
                                </Typography>
                            )}
                        </Link>
                    </Zoom>
                    <Tooltip title={t("actions.closeSidebar")}>
                        <IconButton id={`${closeSidebarButtonName}_${buttonId}`} onClick={() => setOpen(false)}>
                            <ChevronLeftIcon />
                        </IconButton>
                    </Tooltip>
                </StyledSidebarHeader>
                <Divider />
                <List>
                    {open ? (
                        <Box
                            sx={{
                                px: 1,
                            }}
                        >
                            <SearchInput />
                        </Box>
                    ) : (
                        <Tooltip placement="right" title={t("search.name")}>
                            <ListItem
                                sx={{
                                    display: {
                                        sm: "none",
                                        xs: "block",
                                    },
                                }}
                                disablePadding
                            >
                                <ListItemButton
                                    component={Button}
                                    id={`${sidebarId}_${searchButtonName}_${buttonId}`}
                                    sx={{
                                        display: "flex",
                                        justifyContent: "center",
                                    }}
                                    nativeButton
                                    onClick={() => setOpen(true)}
                                >
                                    <ListItemIcon
                                        sx={{
                                            justifyContent: "center",
                                            minWidth: 0,
                                        }}
                                    >
                                        <PageviewIcon />
                                    </ListItemIcon>
                                </ListItemButton>
                            </ListItem>
                        </Tooltip>
                    )}
                    {isLoading
                        ? sidebarData.tabs.map(({ key }) => (
                              <Box
                                  key={key}
                                  sx={{
                                      alignItems: "center",
                                      display: "flex",
                                      flexDirection: "column",
                                      justifyContent: "center",
                                  }}
                              >
                                  <SkeletonLoader count={1} height={50} width={open ? 200 : 50} />
                              </Box>
                          ))
                        : filteredSidebarData.length > 0
                          ? filteredSidebarData.map((tab) => (
                                <SidebarTab
                                    key={tab.id}
                                    activeTab={sidebarActiveTab}
                                    expanded={expandedTabs.includes(tab.id)}
                                    open={open}
                                    setOpen={setOpen}
                                    tab={tab}
                                    onToggle={toggleExpandedTab}
                                />
                            ))
                          : null}
                </List>
            </StyledSidebar>
        </Box>
    );
};

export default Sidebar;

Sidebar.propTypes = {
    open: PropTypes.bool,
    setOpen: PropTypes.func,
};
